import { Injectable, computed, inject, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, tap } from 'rxjs';
import { EnseigneConfig } from './enseigne.model';
import { EnseigneConfigService } from './enseignes.service';
import { ToasterService } from '../../layout/toaster/toaster.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class EnseignesStore {
  // Injected service
  private readonly enseignesService = inject(EnseigneConfigService);
  private readonly toasterService = inject(ToasterService);
  private readonly translate = inject(TranslateService);

  // Triggers for save and delete operations
  private readonly saveOneTrigger = signal<{ config: EnseigneConfig; timestamp: number } | null>(
    null,
  );
  private readonly deleteTrigger = signal<{ codeOrg: string; timestamp: number } | null>(null);

  // Publicly exposed signals
  readonly enseignes = computed(() =>
    (this.enseignesResource.value() ?? []).map((e) => ({
      ...e,
      countriesConfig: (e.countriesConfig ?? []).map((c) => ({
        ...c,
        prefix: c.prefix ?? '',
        transportGroups: c.transportGroups ?? [],
      })),
    })),
  );

  readonly isLoading = computed(
    () =>
      this.enseignesResource.isLoading() ||
      this.deleteResource.isLoading() ||
      this.saveOneResource.isLoading(),
  );
  readonly error = computed(() => this.enseignesResource.error());

  // Resource to load brands from the API
  private readonly enseignesResource = rxResource({
    stream: () => this.enseignesService.getAllEnseignes(),
  });

  // Resource to save one brand configuration
  readonly saveOneResource = rxResource({
    params: () => this.saveOneTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      return this.enseignesService.updateEnseigneConfig([params.config]).pipe(
        tap(() => {
          queueMicrotask(() => {
            this.toasterService.success(this.translate.instant('GLOBAL.SAVE_SUCCESS'));
            this.enseignesResource.reload();
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('GLOBAL.SAVE_ERROR'));
          });
          throw error;
        }),
      );
    },
  });

  // Resource to delete a brand configuration
  readonly deleteResource = rxResource({
    params: () => this.deleteTrigger(),
    stream: ({ params }) => {
      if (!params) return EMPTY;
      return this.enseignesService.deleteEnseigneConfig(params.codeOrg).pipe(
        tap(() => {
          queueMicrotask(() => {
            this.toasterService.success(this.translate.instant('ENSEIGNES.DELETE_SUCCESS'));
            this.enseignesResource.reload();
          });
        }),
        catchError((error) => {
          queueMicrotask(() => {
            this.toasterService.error(this.translate.instant('ENSEIGNES.DELETE_ERROR'));
          });
          throw error;
        }),
      );
    },
  });

  // Mutations
  updateEnseigne(config: EnseigneConfig): void {
    this.saveOneTrigger.set({ config, timestamp: Date.now() });
  }

  deleteEnseigne(codeOrg: string): void {
    this.deleteTrigger.set({ codeOrg, timestamp: Date.now() });
  }

  addEnseigne(config: EnseigneConfig): void {
    this.saveOneTrigger.set({ config, timestamp: Date.now() });
  }
}
